import { useEffect, useRef, useState } from 'react'
import { useKioskStore } from '@/stores/kiosk-store'

const DEFAULT_INACTIVITY_TIMEOUT = 60_000
const DEFAULT_COUNTDOWN_SECONDS = 10

function canResetSession() {
  const { step, processing, adminOpen } = useKioskStore.getState()
  return !processing && !adminOpen && (step === 'browsing' || step === 'cart' || step === 'checkout' || step === 'error')
}

export function useInactivity() {
  const step = useKioskStore((s) => s.step)
  const processing = useKioskStore((s) => s.processing)
  const adminOpen = useKioskStore((s) => s.adminOpen)
  const remoteConfig = useKioskStore((s) => s.remoteConfig)
  const timeout = remoteConfig?.inactivityTimeout ? remoteConfig.inactivityTimeout * 1000 : DEFAULT_INACTIVITY_TIMEOUT
  const countdownSeconds = remoteConfig?.countdownDuration ? Math.ceil(remoteConfig.countdownDuration) : DEFAULT_COUNTDOWN_SECONDS
  const active = !processing && !adminOpen && (step === 'browsing' || step === 'cart' || step === 'checkout' || step === 'error')
  const [showWarning, setShowWarning] = useState(false)
  const [countdown, setCountdown] = useState(countdownSeconds)
  const restartRef = useRef(() => {})

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    let countdownTimer: ReturnType<typeof setInterval> | undefined
    const clearTimers = () => {
      clearTimeout(timer)
      clearInterval(countdownTimer)
    }
    const restart = () => {
      clearTimers()
      setShowWarning(false)
      setCountdown(countdownSeconds)
      if (!active || !canResetSession()) return
      timer = setTimeout(() => {
        if (!canResetSession()) return
        setShowWarning(true)
        let remaining = countdownSeconds
        countdownTimer = setInterval(() => {
          // A payment/admin transition may precede React's effect cleanup.
          if (!canResetSession()) {
            restart()
            return
          }
          remaining -= 1
          setCountdown(remaining)
          if (remaining <= 0) {
            clearTimers()
            setShowWarning(false)
            useKioskStore.getState().resetToAttractor()
          }
        }, 1000)
      }, timeout)
    }
    restartRef.current = restart
    restart()
    window.addEventListener('touchstart', restart, { passive: true })
    window.addEventListener('mousedown', restart)
    window.addEventListener('keydown', restart)
    window.addEventListener('scroll', restart, { passive: true, capture: true })
    return () => {
      clearTimers()
      restartRef.current = () => {}
      window.removeEventListener('touchstart', restart)
      window.removeEventListener('mousedown', restart)
      window.removeEventListener('keydown', restart)
      window.removeEventListener('scroll', restart, true)
    }
  }, [active, countdownSeconds, timeout])

  return { showWarning: active && showWarning, countdown, dismiss: () => restartRef.current() }
}
